import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export default function AuthCallback() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate("/login"); return; }
    const session_id = match[1];
    (async () => {
      try {
        await api.post("/auth/session", { session_id });
        // Clear hash from URL so downstream renders don't re-trigger AuthCallback.
        window.history.replaceState({}, "", "/app/dashboard");
        // Give the browser a beat to persist the httpOnly Set-Cookie before we query /auth/me.
        await wait(150);
        let me = await checkAuth();
        if (!me) {
          await wait(600);
          me = await checkAuth();
        }
        if (!me) throw new Error("session-not-persisted");
        const dest = me.role === "super_admin" ? "/super-admin" : "/app/dashboard";
        navigate(dest, { replace: true });
      } catch (err) {
        // Surface the "not invited" case so the login page can explain
        // what happened - otherwise the user is left staring at a bare
        // login button after clicking the invite link.
        const detail = err?.response?.data?.detail;
        const code = typeof detail === "object" && detail?.error === "not_invited"
          ? "not_invited"
          : (err?.response?.status === 401 ? "not_invited" : "signin_failed");
        navigate(`/login?error=${code}`, { replace: true });
      }
    })();
  }, [navigate, checkAuth]);

  return (
    <div className="min-h-screen bg-navy-deep flex items-center justify-center text-white">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-6" />
        <div className="font-serif text-xl">Signing you in…</div>
        <div className="text-white/50 text-sm mt-2">Establishing secure session</div>
      </div>
    </div>
  );
}
